/**
 * @file ToastContext.tsx
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description Toast notification context provider for the Shay Suite Admin application.
 *              This file manages toast notifications, providing a centralized way to display
 *              success, error, warning, and info messages throughout the application.
 *              It uses Material-UI Alert components for consistent styling.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { AlertColor } from '@mui/material';
import Toast from '../components/Toast';

/**
 * Interface defining the structure of the toast context
 * Provides methods for displaying different types of toast notifications
 */
interface ToastContextType {
  showToast: (message: string, severity: AlertColor) => void;  // Generic toast display method
  showSuccess: (message: string) => void;                      // Display success toast
  showError: (message: string) => void;                        // Display error toast
  showWarning: (message: string) => void;                      // Display warning toast
  showInfo: (message: string) => void;                         // Display info toast
}

/**
 * React context for toast notification management
 * Provides toast methods to child components
 */
const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * Custom hook to access toast context
 * 
 * @returns {ToastContextType} The toast context value
 * @throws {Error} If used outside of ToastProvider
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

/**
 * Props interface for the ToastProvider component
 */
interface ToastProviderProps {
  children: ReactNode;  // Child components to be wrapped by the provider
}

/**
 * Toast provider component that manages notification display
 * 
 * This component:
 * - Manages toast notification state (open/closed, message, severity)
 * - Provides methods for different types of notifications
 * - Renders the Toast component with current state
 * - Handles toast lifecycle (show/hide)
 * 
 * @param {ToastProviderProps} props - Component props containing children
 * @returns {JSX.Element} Context provider wrapping children with toast functionality
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  // Toast state management
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('info');

  /**
   * Generic method to display a toast notification
   * 
   * @param {string} newMessage - The message to display in the toast
   * @param {AlertColor} newSeverity - The severity level of the toast (success, error, warning, info)
   */
  const showToast = (newMessage: string, newSeverity: AlertColor) => {
    setMessage(newMessage);
    setSeverity(newSeverity);
    setOpen(true);
  };

  /**
   * Convenience method to display a success toast
   * 
   * @param {string} message - The success message to display
   */
  const showSuccess = (message: string) => showToast(message, 'success');

  /**
   * Convenience method to display an error toast
   * 
   * @param {string} message - The error message to display
   */
  const showError = (message: string) => showToast(message, 'error');

  /**
   * Convenience method to display a warning toast
   * 
   * @param {string} message - The warning message to display
   */
  const showWarning = (message: string) => showToast(message, 'warning');

  /**
   * Convenience method to display an info toast
   * 
   * @param {string} message - The info message to display
   */
  const showInfo = (message: string) => showToast(message, 'info');

  /**
   * Handles closing the toast notification
   * Called when user dismisses the toast or auto-hide timer expires
   */
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ToastContext.Provider
      value={{
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
      }}
    >
      {children}
      <Toast
        open={open}
        message={message}
        severity={severity}
        onClose={handleClose}
      />
    </ToastContext.Provider>
  );
}; 